import { Inject, Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

import { Account, TransactionType, OrderType, OrderDuration, DeskRouteCode, AccountType, DeskRouteTimeZone, 
         TradeCommission, UserType, OptionStrategy } from '../../models/common/trading-common.model';
import { MultiLegOptionTradingOrder, OptionTradingOrder } from '../../models/option/trading-option.model';
import { TradingEntitlements } from '../../models/common/trading-entitlements';
import { OptionTradingServiceStrategy } from './option-trading-service-strategy.service';

@Injectable()
export class OptionTradingService {
    private commissionOverrideSubject = new BehaviorSubject<TradeCommission | null>(null);
    commissionOverrideSubject$ = this.commissionOverrideSubject.asObservable();

    constructor(@Inject(OptionTradingServiceStrategy) private strategies: Array<OptionTradingServiceStrategy>) {

    }

    private getStrategy(platform: string): OptionTradingServiceStrategy {
        return this.strategies.find((strategy) => {
            return strategy.support(platform);
        });
    }

    public commissionOverrideChangeNotification(tradeCommission: TradeCommission) {
        this.commissionOverrideSubject.next(tradeCommission);
    }

    public getOptionStrategies(account: Account ,platform: string, entitlements: TradingEntitlements): string[] {
        return this.getStrategy(platform).getOptionStrategies(account, entitlements);
    }

    public getOptionMultiLegTransactionTypes(account: Account ,platform: string, entitlements: TradingEntitlements, strategy: OptionStrategy, isAPA?: boolean): TransactionType[] {
        return this.getStrategy(platform).getOptionMultiLegTransactionTypes(account, entitlements, strategy, isAPA);
    }

    public getTransactionTypes(account: Account ,platform: string, entitlements: TradingEntitlements, isAPA: boolean): TransactionType[] {
        return this.getStrategy(platform).getTransactionTypes(account, entitlements, isAPA);
    }

    public getOrderTypes(platform: string, isNonDollar: boolean, qty: number): OrderType[] {
        return this.getStrategy(platform).getOrderTypes(isNonDollar, qty);
    }

    public getOptionMultiLegOrderTypes(platform: string, strategy: OptionStrategy, transactionType: TransactionType, modifier?: any): OrderType[] {
        return this.getStrategy(platform).getOptionMultiLegOrderTypes(strategy, transactionType, modifier);
    }

    public getDurations(platform: string, isMultiLegOrder: boolean, orderType?: string, ordQty?: number, userType?: UserType): OrderDuration[] {
        return this.getStrategy(platform).getDurations(isMultiLegOrder, orderType, ordQty, userType);
    }

    public accountTypes(
        platform: string, account: Account, transactionType: TransactionType, symAccountType: AccountType
    ): AccountType[] {
        return this.getStrategy(platform).accountTypes(account, transactionType, symAccountType);
    }

    public getShortSaleRebate(platform: string, account: string, symbol: string): Observable<any> {
        return this.getStrategy(platform).getShortSaleRebate(account, symbol);
    }

    public getExpiryDate(platform: string, symbol: string): Observable<any> {
        return this.getStrategy(platform).getExpiryDate(symbol);
    }

    public getStrikePrice(platform: string, symbol: string, optExpDate: string): Observable<any> {
        return this.getStrategy(platform).getStrikePrice(symbol, optExpDate);
    }

    public getDeskRouteTimeZone(platform: string, account?: Account): DeskRouteTimeZone[] {
        return this.getStrategy(platform).getDeskRouteTimeZone();
    }

    public getDeskCode(platform: string, account?: Account): DeskRouteCode[] {
        return this.getStrategy(platform).getDeskCode();
    }

    public reviewOptionOrder(platform: string, optionTradingOrder: OptionTradingOrder): Observable<any> {
        return this.getStrategy(platform).reviewOptionOrder(optionTradingOrder);
    }

    public reviewMultiLegOptionOrder(platform: string, multiLegOptionTradingOrder: MultiLegOptionTradingOrder): Observable<any> {
        return this.getStrategy(platform).reviewMultiLegOptionOrder(multiLegOptionTradingOrder);
    }

    public submitOptionOrder(platform: string, optionTradingOrder: OptionTradingOrder): Observable<any> {
        return this.getStrategy(platform).submitOptionOrder(optionTradingOrder);
    }

    public submitMultiLegOptionOrder(platform: string, multiLegOptionTradingOrder: MultiLegOptionTradingOrder): Observable<any> {
        return this.getStrategy(platform).submitMultiLegOptionOrder(multiLegOptionTradingOrder);
    }

    public correctOptionOrder(platform: string, optionTradingOrder: OptionTradingOrder): Observable<any> {
        return this.getStrategy(platform).correctOptionOrder(optionTradingOrder);
    }

    public correctMultiLegOptionOrder(platform: string, multiLegOptionTradingOrder: MultiLegOptionTradingOrder): Observable<any> {
        return this.getStrategy(platform).correctMultiLegOptionOrder(multiLegOptionTradingOrder);
    }

    public getMultiLegCommission(platform: string, multiLegOptionTradingOrder: MultiLegOptionTradingOrder): Observable<any> {
        return this.getStrategy(platform).getMultiLegCommission(multiLegOptionTradingOrder);
    }

    public getMultiLegOrderDetails(platform: string, acctNum: string, ordDtlKey: string, subRequestTypeCode: string, ordProdCodeTex: string, eID: string): Observable<any> {
        return this.getStrategy(platform).getMultiLegOrderDetails(acctNum, ordDtlKey, subRequestTypeCode, ordProdCodeTex, eID);
    }

    public getCodeValuesExchangeList(platform: string): Observable<any> {
        return this.getStrategy(platform).getCodeValuesExchangeList();
    }

    public cancelOptionOrder(platform: string, acctNum: string, ordDtlKey: string, subRequestTypeCode: string): Observable<any> {
        return this.getStrategy(platform).cancelOptionOrder(acctNum, ordDtlKey, subRequestTypeCode);
    }

    public cancelMultiLegOptionOrder(platform: string, acctNum: string, ordDtlKey: string, subRequestTypeCode: string, eID: string): Observable<any> {
      return this.getStrategy(platform).cancelComplexOptionOrder(acctNum, ordDtlKey, subRequestTypeCode, eID);
    }

    public getOptionChainQuotes(platform: string, symbol: string, optExpDate: string, putCallInd: string): Observable<any> {
        return this.getStrategy(platform).getOptionChainQuotes(symbol, optExpDate, putCallInd);
    }
}
